/**
 * Rounds a number to a specified number of decimal places
 *
 * @param n number to be rounded
 * @param digits number of decimal places (default: 2)
 * @returns rounded number
 */
export const roundTo = (n: number, digits = 2) => {
  let scale = 10 ** digits;
  return Math.round(n * scale) / scale;
};

/**
 * Converts minutes to milliseconds
 *
 * @param min minutes to be converted
 * @returns milliseconds
 */
export const minutesToMillisec = (min: number) => min * 60 * 1000;

/**
 * Logarithm base
 *
 * @param n number to be transformed
 * @param base base of the logarithm (default: 10)
 * @returns transformed number
 */
export const logBase = (n: number, base = 10) => Math.log(n) / Math.log(base);

/**
 * Sublinear growth
 *
 * Returns a value that grows more slowly as the input number grows.
 *
 * @param n number to be transformed
 * @param rate rate of reduction (default: 3)
 * @param base base of the logarithm (default: 10)
 * @returns transformed number
 */
export const sublinearGrowth = (n: number, rate = 3, base = 10) => {
  return Math.ceil(n / (1 + logBase(n, base) ** rate));
};

/**
 * Random gaussian value
 *
 * Returns a random value that's more likely to fall closer to the center or the skewed center.
 *
 * @param center center of the distribution (default: 0)
 * @param spread spread of the distribution (default: 1)
 * @param skew skew of the distribution (default: 1)
 * @returns random value
 */
export const randomGaussianValue = (
  center = 0,
  spread = 1,
  skew = 1
): number => {
  const stdDev = spread / 2 / 3;

  const u0 = Math.random();
  const u1 = Math.random();
  const z0 = Math.sqrt(-2.0 * Math.log(u0)) * Math.cos(2.0 * Math.PI * u1);
  const z1 = Math.sqrt(-2.0 * Math.log(u0)) * Math.sin(2.0 * Math.PI * u1);

  // Apply Azzalini’s skew transformation
  const delta = skew / Math.sqrt(1 + skew * skew);
  const skewed = delta * Math.abs(z0) + Math.sqrt(1 - delta * delta) * z1;

  return center + skewed * stdDev;
};

/**
 * Checks if a point is inside an elipsis
 *
 * @param x x coordinate of the point
 * @param y y coordinate of the point
 * @param width width of the elipsis
 * @param height height of the elipsis
 * @param cx x coordinate of the center of the elipsis (default: 0)
 * @param cy y coordinate of the center of the elipsis (default: 0)
 * @returns true if the point is inside the elipsis, false otherwise
 */
export const isInsideElipsis = (
  x: number,
  y: number,
  width: number,
  height: number,
  cx = 0,
  cy = 0
) => {
  const dx = x - cx;
  const dy = y - cy;
  const a = width / 2;
  const b = height / 2;

  return (dx * dx) / (a * a) + (dy * dy) / (b * b) <= 1;
};
